// Shared blog article renderer used BOTH by the public BlogPost page and
// by the admin Preview modal. Extracting it keeps the editor 100% honest —
// what admin previews is exactly what visitors see.
//
// Markdown-lite grammar (unchanged for existing posts):
//   ## Subtítulo
//   ### Subtítulo menor (não usado pelo editor por blocos, mas mantido para
//        retrocompatibilidade com artigos antigos escritos à mão)
//   - item de lista (uma ou mais linhas consecutivas)
//   !! frase de destaque (uma ou mais linhas consecutivas)
//   ![alt](fileId-ou-URL) numa linha isolada → figura
//   [label](url) e **negrito** inline em qualquer parágrafo/lista/subtítulo
//
// A grammar strictly parses paragraphs separated by BLANK lines, so line
// breaks WITHIN a paragraph are preserved via <br /> — matching the current
// behaviour that has already been visually validated in production.

import { fileUrl } from "@/lib/api";
import { renderBold } from "@/lib/markdown";

const IMG_RE = /^!\[([^\]]*)\]\(([^)]+)\)\s*$/;

export function renderContent(text) {
    if (!text) return null;
    const blocks = text.split(/\n\s*\n/);
    return blocks.map((block, i) => {
        const lines = block.split("\n").filter((l) => l.length);
        if (!lines.length) return null;
        // Image on its own line → figure. Detected FIRST so it wins over
        // paragraph fallback.
        if (lines.length === 1 && IMG_RE.test(lines[0])) {
            const [, alt, src] = IMG_RE.exec(lines[0]);
            // Bare UUID → resolve via files endpoint; explicit URLs pass through.
            const resolved = /^https?:\/\/|^\/api\/files\//.test(src) ? src : fileUrl(src);
            return (
                <figure key={i} className="my-10" data-testid="blog-image">
                    <img src={resolved} alt={alt || ""} className="w-full rounded-xl bg-stone-100" loading="lazy" />
                </figure>
            );
        }
        // H2
        if (lines.length === 1 && /^##\s+/.test(lines[0])) {
            return (
                <h2 key={i} className="font-display text-2xl sm:text-3xl font-black text-stone-900 mt-14 mb-5 tracking-tight leading-[1.15]">
                    {renderBold(lines[0].replace(/^##\s+/, ""))}
                </h2>
            );
        }
        // H3
        if (lines.length === 1 && /^###\s+/.test(lines[0])) {
            return (
                <h3 key={i} className="font-display text-xl sm:text-2xl font-bold text-stone-900 mt-10 mb-4 tracking-tight">
                    {renderBold(lines[0].replace(/^###\s+/, ""))}
                </h3>
            );
        }
        // Pull-quote
        if (lines.every((l) => /^!!\s+/.test(l))) {
            return (
                <div key={i} className="my-10 flex gap-5" data-testid="blog-highlight">
                    <span aria-hidden className="w-[3px] flex-shrink-0 bg-[hsl(var(--copper))] rounded-full" />
                    <p className="font-display text-xl sm:text-2xl font-bold text-stone-900 leading-[1.35]">
                        {lines.map((l, j) => (
                            <span key={j}>
                                {renderBold(l.replace(/^!!\s+/, ""))}
                                {j < lines.length - 1 && <br />}
                            </span>
                        ))}
                    </p>
                </div>
            );
        }
        // Unordered list
        if (lines.every((l) => /^-\s+/.test(l))) {
            return (
                <ul key={i} className="list-disc pl-6 space-y-2 text-lg text-stone-700 leading-relaxed my-6">
                    {lines.map((l, j) => <li key={j}>{renderInline(l.replace(/^-\s+/, ""))}</li>)}
                </ul>
            );
        }
        // Paragraph
        return (
            <p key={i} className="text-[17px] sm:text-lg text-stone-700 leading-[1.75] my-5 break-words">
                {lines.map((l, j) => (
                    <span key={j}>
                        {renderInline(l)}
                        {j < lines.length - 1 && <br />}
                    </span>
                ))}
            </p>
        );
    });
}

/* Inline renderer: [label](url) links + **bold**. */
export function renderInline(text) {
    if (!text) return text ?? null;
    const parts = String(text).split(/(\[[^\]]+\]\([^)]+\))/g);
    return parts.map((p, i) => {
        const m = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(p);
        if (m) {
            const [, label, href] = m;
            const isExternal = /^https?:\/\//i.test(href);
            return (
                <a
                    key={i}
                    href={href}
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    className="text-[hsl(var(--copper))] underline underline-offset-4 hover:opacity-80 break-words"
                >
                    {renderBold(label)}
                </a>
            );
        }
        return <span key={i}>{renderBold(p)}</span>;
    });
}
